/**
 * Makes a configured-safe cart line behave correctly on the real cart page.
 *
 * The line is a real Shopify cart item (so it persists like any other cart line, survives
 * reload, and counts toward the cart icon) added against a product's placeholder variant.
 * That variant can't carry the real computed price without Shopify Plus, so this script:
 *   1. Finds any cart line carrying a "_configurationId" property (set by
 *      product-configurator-liberty.js when it adds the line).
 *   2. Overwrites that line's displayed price and the cart total with the real price,
 *      fetched from the same backend that prices checkout — never invented client-side.
 *   3. Intercepts the native checkout button (and hides accelerated/quick-checkout buttons,
 *      which would bypass the interception) so checkout always goes through the
 *      configurator's own draft-order flow instead of native checkout at the placeholder price.
 *
 * Generic by design: it keys off the "_configurationId"/"_configurator" properties only,
 * never a brand or product name, so it works for every model/brand without changes.
 */
(function () {
  const REFRESH_DEBOUNCE_MS = 150;

  function money(n) {
    return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  async function fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) throw new Error('Could not read cart');
    return res.json();
  }

  function configuratorItems(cart) {
    return (cart.items || []).filter((item) => item.properties && item.properties._configurator === 'true');
  }

  async function priceForItem(item) {
    const props = item.properties;
    const apiBase = window.__lcApiBase;
    if (!apiBase || !props._configurationId || !props._sessionToken) return null;
    try {
      const res = await fetch(apiBase + '/configurator-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectionId: props._configurationId, sessionToken: props._sessionToken }),
      });
      const status = await res.json();
      if (!status || !status.found) return null;
      return parseFloat(status.price);
    } catch (error) {
      console.warn('configurator-cart-line: failed to fetch status', error);
      return null;
    }
  }

  function overrideLinePrice(item, realPrice) {
    const row = document.querySelector('tr[data-key="' + item.key + '"]');
    if (!row) return;

    // Line total (the "Product total" column).
    const totalEl = row.querySelector('.cart-items__price text-component');
    if (totalEl) {
      const text = money(realPrice * item.quantity);
      totalEl.textContent = text;
      totalEl.setAttribute('value', text);
      totalEl.classList.remove('lc-price-skeleton');
    }

    // Per-unit price shown next to the product details (a separate element from the line
    // total above — easy to miss since Shopify labels it with a visually-hidden "Price" tag).
    const unitPriceEl = row.querySelector('.cart-items__details > div > span:not(.visually-hidden)');
    if (unitPriceEl) {
      unitPriceEl.textContent = money(realPrice);
      unitPriceEl.classList.remove('lc-price-skeleton');
    }
  }

  function overrideCartBubble(cart) {
    const isEmpty = (cart.item_count || 0) === 0;

    const bubble = document.querySelector('[ref="cartBubble"], .cart-bubble');
    const countEl = document.getElementById('cart-bubble-text') || document.querySelector('[ref="cartBubbleCount"]');
    if (bubble && countEl) {
      bubble.classList.toggle('visually-hidden', isEmpty);
      const countSpan = countEl.querySelector('[ref="cartBubbleCount"]') || countEl;
      countSpan.textContent = String(cart.item_count);
      countSpan.classList.toggle('hidden', isEmpty);
    }

    // safeMarket-nav-bar.liquid's own cart icon badge — separate markup from the theme's
    // default header-actions cart-bubble above, updated the same way for consistency.
    document.querySelectorAll('[data-sl-cart-count]').forEach((el) => {
      el.textContent = String(cart.item_count);
      el.hidden = isEmpty;
    });
  }

  function overrideCartTotal(cart, priceByKey) {
    const totalEl = document.querySelector('text-component[data-cart-subtotal]');
    if (!totalEl) return;
    let total = 0;
    for (const item of cart.items || []) {
      if (item.properties && item.properties._configurator === 'true' && priceByKey.has(item.key)) {
        total += priceByKey.get(item.key) * item.quantity;
      } else {
        total += item.final_line_price / 100;
      }
    }
    const text = money(total);
    totalEl.textContent = text;
    totalEl.setAttribute('value', text);
    totalEl.classList.remove('lc-price-skeleton');
  }

  async function notifyAbandoned(item) {
    const apiBase = window.__lcApiBase;
    const props = item.properties;
    if (!apiBase || !props._configurationId || !props._sessionToken) return;
    try {
      await fetch(apiBase + '/configurator-abandon', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selectionId: props._configurationId, sessionToken: props._sessionToken }),
      });
    } catch (error) {
      console.warn('configurator-cart-line: failed to notify abandon', error);
    }
  }

  // "Edit configuration" pencil button (cart-products.liquid). Best-effort cancels any draft
  // order already created for this selection — see abandonConfiguredSelection.ts, which is a
  // no-op-ish status flip if checkout was never started, or a real draftOrderDelete if it was
  // — then removes THIS cart line (the abandon call only flips the backend record, it never
  // touches the real Shopify cart, so skipping this step leaves a real cart line pointing at a
  // now-abandoned selection: invisible until the shopper hits checkout without finishing a
  // fresh add-to-cart, at which point configurator-checkout 400s with "Selection is abandoned
  // and can no longer be checked out" — this happened in production, see conversation), then
  // sends the shopper back to the product page with the saved selection/session so
  // product-configurator-{liberty,amsec}.js can restore every prior pick instead of starting
  // blank. Each step is awaited and independently try/caught so a failure in one doesn't skip
  // the next — worst case the shopper still lands on the PDP to reconfigure, which is safe.
  function wireEditConfiguration() {
    document.addEventListener('click', async (e) => {
      const btn = e.target.closest('.cart-items__edit-configuration');
      if (!btn || btn.disabled) return;
      e.preventDefault();

      const editUrl = btn.dataset.editUrl;
      const lineKey = btn.dataset.lineKey;
      const selectionId = btn.dataset.configurationId;
      const sessionToken = btn.dataset.sessionToken;
      if (!editUrl) return;

      btn.disabled = true;
      const apiBase = window.__lcApiBase;

      try {
        if (apiBase && selectionId && sessionToken) {
          await fetch(apiBase + '/configurator-abandon', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ selectionId, sessionToken }),
          });
        }
      } catch (error) {
        console.warn('configurator-cart-line: failed to abandon selection before edit', error);
      }

      try {
        if (lineKey) {
          await fetch('/cart/change.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ id: lineKey, quantity: 0 }),
          });
        }
      } catch (error) {
        console.warn('configurator-cart-line: failed to remove cart line before edit', error);
      }

      const url = new URL(editUrl, window.location.origin);
      if (selectionId && sessionToken) {
        url.searchParams.set('restore_selection', selectionId);
        url.searchParams.set('restore_token', sessionToken);
      }
      window.location.href = url.toString();
    });
  }

  function hideAcceleratedCheckout() {
    document.querySelectorAll('.additional-checkout-buttons, shopify-accelerated-checkout-cart').forEach((el) => {
      el.style.display = 'none';
    });
  }

  function selectionsPayload(items) {
    return JSON.stringify(
      items.map((item) => ({
        selectionId: item.properties._configurationId,
        sessionToken: item.properties._sessionToken,
      }))
    );
  }

  function describePriceChanges(changes) {
    return changes
      .map((c) => `$${c.oldPrice} → $${c.newPrice}`)
      .join(', ');
  }

  let interceptWired = false;
  function wireCheckoutIntercept(getConfigState) {
    if (interceptWired) return;
    interceptWired = true;
    const checkoutBtn = document.getElementById('checkout');
    if (!checkoutBtn) return;

    checkoutBtn.addEventListener(
      'click',
      async (e) => {
        // Runs before the configurator branch below decides to preventDefault() and skip
        // straight to the draft-order invoice flow — without this, a configured-safe cart
        // never goes through native form submission at all, so the cart's required terms
        // checkbox (form="cart-form") never gets its constraint validation checked or shown.
        // reportValidity() checks every form-associated control (the checkbox included) and,
        // if invalid, fires the same 'invalid' event/message as a real submit attempt would.
        if (checkoutBtn.form && !checkoutBtn.form.reportValidity()) {
          e.preventDefault();
          e.stopImmediatePropagation();
          return;
        }

        const state = getConfigState();
        if (!state || !state.items || state.items.length === 0) return; // no configurator line — let native checkout proceed
        e.preventDefault();
        e.stopImmediatePropagation();

        const originalText = checkoutBtn.textContent;
        checkoutBtn.disabled = true;
        checkoutBtn.textContent = 'Preparing checkout...';

        try {
          const cart = await fetchCart();
          const apiBase = window.__lcApiBase;
          const response = await fetch(apiBase + '/configurator-checkout', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              selections: selectionsPayload(state.items),
              cart: JSON.stringify(cart),
            }),
          });
          if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(text || response.statusText);
          }
          const result = await response.json();

          if (result.priceChanged) {
            const proceed = confirm(
              `The price changed for one or more of your configured safes (${describePriceChanges(
                result.changes || []
              )}). Continue to checkout at the new price?`
            );
            checkoutBtn.disabled = false;
            checkoutBtn.textContent = originalText;
            if (proceed) {
              const retry = await fetch(apiBase + '/configurator-checkout', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  selections: selectionsPayload(state.items),
                  cart: JSON.stringify(cart),
                  acknowledgePriceChange: true,
                }),
              });
              const retryResult = await retry.json();
              if (retryResult.invoiceUrl) window.location.href = retryResult.invoiceUrl;
            }
            return;
          }

          if (!result.invoiceUrl) throw new Error(result.error || 'Failed to start checkout');
          window.location.href = result.invoiceUrl;
        } catch (error) {
          console.error('configurator-cart-line: checkout failed', error);
          alert('Sorry, there was a problem starting checkout. Please try again.');
          checkoutBtn.disabled = false;
          checkoutBtn.textContent = originalText;
        }
      },
      { capture: true }
    );
  }

  let refreshTimer = null;
  let latestState = { items: [] };
  let observer = null;

  async function refresh() {
    let cart;
    try {
      cart = await fetchCart();
    } catch (error) {
      return;
    }

    const items = configuratorItems(cart);

    const priceByKey = new Map();
    for (const item of items) {
      const price = await priceForItem(item);
      if (price != null) priceByKey.set(item.key, price);
    }

    // A configured line that was present last refresh but is gone now (shopper removed it, or
    // its quantity hit zero) — if checkout was already started for it, cancel the orphaned
    // draft order. Diffed per-item so removing just one of several configured safes doesn't
    // wait for the whole cart to empty out.
    const currentKeys = new Set(items.map((item) => item.key));
    const removedItems = latestState.items.filter((item) => !currentKeys.has(item.key));
    for (const item of removedItems) notifyAbandoned(item);

    // Our own DOM writes below would otherwise re-trigger the MutationObserver that called
    // us, causing an endless refresh loop — disconnect while writing, reconnect after.
    if (observer) observer.disconnect();
    try {
      overrideCartBubble(cart);
      if (items.length === 0) {
        latestState = { items: [] };
        return;
      }
      for (const item of items) {
        if (priceByKey.has(item.key)) overrideLinePrice(item, priceByKey.get(item.key));
      }
      overrideCartTotal(cart, priceByKey);
      hideAcceleratedCheckout();
    } finally {
      if (observer) observer.observe(document.getElementById('cart-form') || document.body, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    latestState = { items };
    wireCheckoutIntercept(() => latestState);
  }

  function scheduleRefresh() {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, REFRESH_DEBOUNCE_MS);
  }

  function init() {
    const root = document.getElementById('configurator-cart-line-data');
    window.__lcApiBase = (root && root.dataset.apiBase) || '';

    // Re-apply overrides whenever the cart DOM re-renders (quantity change, discount, etc.) —
    // Horizon's cart uses reactive DOM updates that would otherwise wipe our overridden text.
    const cartRoot = document.getElementById('cart-form') || document.body;
    observer = new MutationObserver(scheduleRefresh);
    observer.observe(cartRoot, { childList: true, subtree: true, characterData: true });

    wireEditConfiguration();
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
